import type { ReactNode } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import styled from "styled-components";
import { Button, InlineAlert } from "suomifi-ui-components";
import { voiKasitella } from "../utils/oikeudet";
import { IconArrowRight, IconCheck } from "suomifi-icons";
import type { Kayttaja } from "../types";
import {
  naytaNimi,
  onTunnistautumaton,
  vahvistusStatus,
} from "../utils/kayttajaNimi";
import { alpha2ToAlpha3 } from "../utils/kansalaisuudet";
import countries from "i18n-iso-countries";
import fi from "i18n-iso-countries/langs/fi.json";
import en from "i18n-iso-countries/langs/en.json";
import HenkilollisyysBadge from "./HenkilollisyysBadge";
import TilaBadge from "./TilaBadge";
import UidCopy from "./UidCopy";
import { useTodellinenRooli } from "../hooks/useTodellinenRooli";
import {
  useGetTukipyynnotByKayttajaQuery,
  useAssignAllByKayttajaMutation,
  useUnassignTicketMutation,
} from "../api/backendApi";
import { skipToken } from "@reduxjs/toolkit/query";
import { useTranslation } from "react-i18next";

/**
 * Yhtenäinen käyttäjän perustietojen sivupalkki.
 *
 * Käytössä tukipyyntönäkymässä JA käyttäjäkortilla — sama tyyli ja sijainti
 * molemmissa, jotta virkailijan visuaalinen kokemus on yhtenäinen
 * (20.5.2026 työpaja, D2/C10/H2).
 *
 * Sisältö (rivit ylhäältä alas):
 * - Käyttäjän nimi (tai sähköposti tunnistautumattomilla)
 * - Sähköposti (jos nimi on näkyvissä — välttää duplikaatin)
 * - UID + vahvistusstatus harmaalla alatekstinä
 * - Maa (jos asiakirja olemassa)
 * - Asiakirjan voimassaolo (jos asiakirja olemassa)
 * - Aktiivinen laite -tag (jos käyttäjällä on aktiivinen laite)
 * - HenkilollisyysBadge
 * - Lukitustiedot (jos lukittu)
 * - Duplikaattivaroitus (jos duplikaatti)
 * - "Avaa täysi käyttäjäkortti" -painike (vain jos naytaTaysiKortti)
 */

interface Props {
  kayttaja: Kayttaja;
  /** True tukipyyntönäkymässä → näyttää painikkeen täydelle käyttäjäkortille. */
  naytaTaysiKortti?: boolean;
  /** Mistä tukipyynnöstä saavuttiin (navigointia varten). Lisäksi tämä
   * suodatetaan pois "Saman käyttäjän avoimet" -listalta — listataan vain
   * MUUT avoimet tukipyynnöt. */
  fromTukipyyntoId?: string;
  /** True tukipyyntönäkymässä → näyttää saman käyttäjän muut avoimet
   * tukipyynnöt + "Ota kaikki käsittelyyn" -painikkeen (workshop C11). */
  naytaSamanKayttajanAvoimet?: boolean;
  /** True käyttäjäkortilla → näyttää "Tukipyyntöjä jonossa" -tagin
   * Badges-osiossa (workshop 1.6.2026 D1–D3). Häviää kun jono = 0.
   * Vain visuaalinen huomionherätin — ei klikattava. */
  naytaJonossaTagi?: boolean;
  /** Käyttäjäkortin toimintopainikkeet (Todenna / Lukitse / Vapauta / Poista).
   * Renderöidään sivupalkin yläosaan, jotta ne ovat sivupalkin sisällä eikä
   * erillisenä toolbarina heron alla. */
  toiminnotSlot?: ReactNode;
}

function formatPaiva(iso: string | null, locale: string): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleDateString(locale, {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

const Card = styled.aside`
  background: ${({ theme }) => theme.colors.whiteBase};
  border: 1px solid ${({ theme }) => theme.colors.depthLight1};
  padding: ${({ theme }) => theme.spacing.m};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.m};
`;

const SectionLabel = styled.div`
  font: ${({ theme }) => theme.typography.bodySemiBoldSmall};
  font-size: 0.75rem;
  color: ${({ theme }) => theme.colors.brandBase};
  text-transform: uppercase;
  letter-spacing: 0.08em;
  margin-bottom: ${({ theme }) => theme.spacing.xs};
`;

/* Klikattava käyttäjä-otsikko: koko rivi (KÄYTTÄJÄ + nuoli-ikoni) on
   yhtenäinen Link. Käytetään react-router-dom:n Linkkiä jotta saavutettavuus
   on oikein (semanttinen <a>, näppäimistö, ruudunlukijat — workshop
   WCAG-päätös 2.6.2026). */
const KayttajaOtsikkoLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xxs};
  text-decoration: none;
  margin-bottom: ${({ theme }) => theme.spacing.xs};
  color: ${({ theme }) => theme.colors.brandBase};

  & > span {
    margin-bottom: 0;
  }

  & > svg {
    width: 1rem;
    height: 1rem;
  }

  &:hover {
    text-decoration: underline;
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.highlightBase};
    outline-offset: 2px;
  }
`;

const Name = styled.div`
  font: ${({ theme }) => theme.typography.bodySemiBold};
  font-size: 1rem;
  color: ${({ theme }) => theme.colors.blackBase};
`;

/* Kenttärivi 2-col-layoutina: label vasemmalla (kiinteä leveys), arvo
   oikealla (täyttää loput). Tiivistys vrt. nykyinen stacked-pinoaminen
   noin 50 %. Label-sarake 10rem — mahduttaa pisin label "Viim.
   tunnistautuminen" (lyhennetty "Viimeisin" → "Viim." jotta mahtuu
   yhdelle riville). Pitkät arvot saavat rivittyä (word-break).
   Suomi.fi:n konventio asiointi- ja tiedonnäyttönäkymissä. */
const FieldRow = styled.div`
  display: grid;
  grid-template-columns: 10rem 1fr;
  gap: ${({ theme }) => theme.spacing.s};
  margin-top: ${({ theme }) => theme.spacing.s};
  align-items: start;
`;

/* Labelin tyyli (1.6.2026 työpaja-päätös): isolla alkukirjaimella eikä caps,
   sama välistys kuin arvolla, semibold-painikko DS:n TextInput-labelin
   tyylillä. Arvo oikealla on regular — label visuaalisesti vahvempi. */
const FieldLabel = styled.div`
  font: ${({ theme }) => theme.typography.bodySemiBoldSmall};
  font-size: 0.8125rem;
  color: ${({ theme }) => theme.colors.blackBase};
`;

const FieldValue = styled.div`
  font: ${({ theme }) => theme.typography.bodyTextSmall};
  font-size: 0.8125rem;
  color: ${({ theme }) => theme.colors.blackBase};
  word-break: break-word;
`;

/* UID + Vahvistettu-teksti tiukempana sisäkkäisenä rivinä — omassa
   wrapperissa jotta vahvistus-status pysyy UID:n alarivinä, ei mene
   2-col-grid:in seuraavalle riville. insetXxs-gap on lähellä mutta
   ei kiinni. */
const UidArea = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.insetXxs};
`;

const VahvistusHint = styled.div<{ $todennettu: boolean }>`
  font: ${({ theme }) => theme.typography.bodyTextSmall};
  font-size: 0.75rem;
  /* successDark1 vahvistetulle (kontrasti valkoista vasten ~5.7:1, AA ✓),
     depthDark1 ei-vahvistetulle (~4.8:1, AA ✓). */
  color: ${({ theme, $todennettu }) =>
    $todennettu ? theme.colors.successDark1 : theme.colors.depthDark1};
`;

const AktiivinenLaiteTag = styled.div`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xxs};
  align-self: flex-start;
  background: ${({ theme }) => theme.colors.successSecondary};
  color: ${({ theme }) => theme.colors.successDark1};
  padding: ${({ theme }) => theme.spacing.xxs} ${({ theme }) => theme.spacing.s};
  font: ${({ theme }) => theme.typography.actionElementInnerTextBold};
  font-size: 0.75rem;
  line-height: 1.4;

  & > svg {
    width: 0.875rem;
    height: 0.875rem;
  }
  /* IconCheck:n SVG-path on hardcoded fill #222 — pakotetaan successDark1
     jotta check näkyy yhtenäisesti tag:n tekstin kanssa. */
  & > svg path {
    fill: ${({ theme }) => theme.colors.successDark1};
  }
`;

const Badges = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing.xs};
`;

/* Tukipyyntöjä jonossa -tagi (workshop 1.6.2026 D1–D3). Visuaalinen
   huomionherätin kun käyttäjällä on avoimia/käsittelyssä-tilaisia
   pyyntöjä. Numero on osa tekstiä samalla värillä — sama mitoitus ja
   korkeus kuin muilla badgeillä (HenkilollisyysBadge, AktiivinenLaiteTag).
   Ei klikattava. */
/* K5 3g: "Ei aktiivista laitetta" -badge kun käyttäjällä ei ole yhtään
   aktiivista laitetta. Taikanappi kytkeytyy tähän tilanteeseen. */
const EiAktiivistaLaitettaTag = styled.div`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xxs};
  align-self: flex-start;
  background: ${({ theme }) => theme.colors.depthLight3};
  color: ${({ theme }) => theme.colors.depthDark1};
  padding: ${({ theme }) => theme.spacing.xxs} ${({ theme }) => theme.spacing.s};
  font: ${({ theme }) => theme.typography.actionElementInnerTextBold};
  font-size: 0.75rem;
  line-height: 1.4;
`;

const JonossaTag = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xxs};
  background: ${({ theme }) => theme.colors.alertLight1};
  color: ${({ theme }) => theme.colors.alertBase};
  padding: ${({ theme }) => theme.spacing.xxs} ${({ theme }) => theme.spacing.s};
  font: ${({ theme }) => theme.typography.actionElementInnerTextBold};
  font-size: 0.75rem;
  line-height: 1.4;
  white-space: nowrap;
  flex-shrink: 0;
`;

/* ID-proofing lukittu -tagi — samaa mitoitusta ja tyyliä kuin JonossaTag,
   alert-värimaailmalla. Pieni badge sivupalkin Badges-rivillä; tarkat
   tiedot (yritysmäärä, vapautumisaika) löytyvät Henkilöllisyyden todentaminen
   -kortin ylälaidasta. */
const LukittuTag = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xxs};
  background: ${({ theme }) => theme.colors.alertLight1};
  color: ${({ theme }) => theme.colors.alertBase};
  padding: ${({ theme }) => theme.spacing.xxs} ${({ theme }) => theme.spacing.s};
  font: ${({ theme }) => theme.typography.actionElementInnerTextBold};
  font-size: 0.75rem;
  line-height: 1.4;
  white-space: nowrap;
  flex-shrink: 0;
`;

interface NoteBoxProps {
  $variant: "alert" | "warning";
}

const NoteBox = styled.div<NoteBoxProps>`
  background: ${({ theme, $variant }) =>
    $variant === "alert"
      ? theme.colors.alertLight1
      : theme.colors.warningLight1};
  border-left: 4px solid
    ${({ theme, $variant }) =>
      $variant === "alert" ? theme.colors.alertBase : theme.colors.warningBase};
  padding: ${({ theme }) => theme.spacing.s};
`;

const NoteTitle = styled.div<NoteBoxProps>`
  font: ${({ theme }) => theme.typography.bodySemiBoldSmall};
  font-size: 0.75rem;
  color: ${({ theme, $variant }) =>
    $variant === "alert" ? theme.colors.alertBase : theme.colors.blackBase};
  margin-bottom: ${({ theme }) => theme.spacing.xxs};
`;

/* Poistetun käyttäjän InlineAlertin aikaleimarivit. */
const PoistettuRivi = styled.div`
  font: ${({ theme }) => theme.typography.bodyTextSmall};
  font-size: 0.8125rem;
  color: ${({ theme }) => theme.colors.blackBase};
  & + & {
    margin-top: ${({ theme }) => theme.spacing.xxs};
  }
`;

/* "Poistettu"-pilleri Badges-riville. Sama keltainen warning-väri kuin
   poisto-InlineAlertilla (warningLight1 -tausta + musta teksti), jotta pilleri
   ja huomautus ovat väripohjaisesti johdonmukaiset. */
const PoistettuTag = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xxs};
  background: ${({ theme }) => theme.colors.warningLight1};
  color: ${({ theme }) => theme.colors.blackBase};
  padding: ${({ theme }) => theme.spacing.xxs} ${({ theme }) => theme.spacing.s};
  font: ${({ theme }) => theme.typography.actionElementInnerTextBold};
  font-size: 0.75rem;
  line-height: 1.4;
  white-space: nowrap;
  flex-shrink: 0;
`;

const DuplicateLink = styled.button`
  background: transparent;
  border: 0;
  padding: 0;
  font-family: "Courier New", monospace;
  font-size: 0.75rem;
  color: ${({ theme }) => theme.colors.highlightBase};
  cursor: pointer;

  &:hover {
    color: ${({ theme }) => theme.colors.highlightDark1};
    text-decoration: underline;
  }
`;

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  padding-top: ${({ theme }) => theme.spacing.s};
  border-top: 1px solid ${({ theme }) => theme.colors.depthLight2};
`;

/* WCAG: tämä navigoi toiseen sivuun, joten kuuluu olla <a>/<Link>, ei
   <button>. DS-tyyli: alleviivaus vain hoverissa, ei oletuksena. */
const FullKortinLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xxs};
  color: ${({ theme }) => theme.colors.highlightBase};
  font: ${({ theme }) => theme.typography.bodySemiBoldSmall};
  font-size: 0.8125rem;
  text-decoration: none;

  &:hover {
    color: ${({ theme }) => theme.colors.highlightDark1};
    text-decoration: underline;
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.highlightBase};
    outline-offset: 2px;
  }

  & > svg {
    width: 0.875rem;
    height: 0.875rem;
  }
`;

const SamanKayttajanWrap = styled.div`
  padding-top: ${({ theme }) => theme.spacing.s};
  border-top: 1px solid ${({ theme }) => theme.colors.depthLight2};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.s};
`;

const SamanKayttajanTitle = styled.div`
  font: ${({ theme }) => theme.typography.bodySemiBoldSmall};
  font-size: 0.6875rem;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: ${({ theme }) => theme.colors.depthDark1};
`;

const AvoinList = styled.div`
  display: flex;
  flex-direction: column;
`;

/* Nykyinen tukipyyntö merkitään visuaalisesti — ei tekstillä. UX-tutkimus
   (Linear/Jira/Gmail/Notion-pattern): tausta-fill + vasemman reunan
   väripalkki + bold typografia. Sama pattern kuin SideNavin aktiivinen
   linkki, jolloin käyttäjä tunnistaa kuvion tutuksi "tässä olen nyt"
   -merkinnäksi. */
const AvoinRow = styled.button<{ $nykyinen: boolean }>`
  background: ${({ theme, $nykyinen }) =>
    $nykyinen ? theme.colors.highlightLight4 : "transparent"};
  border: 0;
  border-left: ${({ theme, $nykyinen }) =>
    $nykyinen
      ? `3px solid ${theme.colors.brandBase}`
      : "3px solid transparent"};
  padding: ${({ theme }) => theme.spacing.xs};
  text-align: left;
  cursor: ${({ $nykyinen }) => ($nykyinen ? "default" : "pointer")};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xxs};
  font: inherit;
  width: 100%;

  & + & {
    margin-top: 1px;
  }

  &:hover {
    background: ${({ theme, $nykyinen }) =>
      $nykyinen ? theme.colors.highlightLight4 : theme.colors.highlightLight4};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.highlightBase};
    outline-offset: -2px;
  }
`;

const AvoinTopRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs};
  width: 100%;
`;

const AvoinTiketti = styled.span<{ $nykyinen: boolean }>`
  font-family: "Courier New", monospace;
  font-size: 0.6875rem;
  color: ${({ theme }) => theme.colors.brandBase};
  font-weight: ${({ $nykyinen }) => ($nykyinen ? 700 : 600)};
  flex-shrink: 0;
`;

const AvoinAihe = styled.span<{ $nykyinen: boolean }>`
  font-size: 0.8125rem;
  color: ${({ theme }) => theme.colors.blackBase};
  font-weight: ${({ $nykyinen }) => ($nykyinen ? 700 : 400)};
`;

/* Spacer pakottaa TilaBadgen rivin oikeaan laitaan. */
const AvoinSpacer = styled.span`
  flex: 1;
`;

const EmptyAvoimet = styled.div`
  font: ${({ theme }) => theme.typography.bodyTextSmall};
  font-size: 0.75rem;
  color: ${({ theme }) => theme.colors.depthDark1};
  font-style: italic;
`;

/* Sivupalkin toimintopainikkeet: pienempi koko kuin DS-oletus, koska
   portaali on hiirellä käytettävä ammattilaistyökalu — ei kosketukseen
   optimoitu. Käyttäjäkortilla painikkeet pinottuna täysleveinä, jotta
   reunat asettuvat samalle linjalle (erilaisista tekstipituuksista
   huolimatta). CSS-override kohdistuu DS:n .fi-button -luokkaan. */
const ToiminnotWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xxs};
  padding-top: ${({ theme }) => theme.spacing.s};
  border-top: 1px solid ${({ theme }) => theme.colors.depthLight2};

  & .fi-button {
    width: 100%;
    min-height: 28px;
    padding: 4px 10px;
    font-size: 0.8125rem;
  }
  & .fi-button .fi-icon {
    width: 0.875rem;
    height: 0.875rem;
  }
`;

export default function KayttajaSidebar({
  kayttaja,
  naytaTaysiKortti,
  fromTukipyyntoId,
  naytaSamanKayttajanAvoimet,
  naytaJonossaTagi,
  toiminnotSlot,
}: Props) {
  const { t, i18n } = useTranslation(["kayttajaSideBar", "common"]);
  const locale = i18n.language === "en" ? "en-GB" : "fi-FI";
  const navigate = useNavigate();
  const location = useLocation();

  countries.registerLocale(fi);
  countries.registerLocale(en);

  const {
    kayttaja: virkailija,
    todellinenRooli,
    isLoading,
  } = useTodellinenRooli();
  const voikoKasitella = voiKasitella(todellinenRooli?.profiili);

  const tarvitaanTukipyynnot = naytaSamanKayttajanAvoimet || naytaJonossaTagi;
  const { data: kayttajanTukipyynnot = [] } = useGetTukipyynnotByKayttajaQuery(
    tarvitaanTukipyynnot ? kayttaja.uid : skipToken,
  );
  const [assignAll] = useAssignAllByKayttajaMutation();
  const [unassignTicket] = useUnassignTicketMutation();

  /* Sibling-navigaatio toiseen tukipyyntöön (saman käyttäjän avoimet -listalla
     oikealla palkissa): kuljetetaan listSearch + listScrollY eteenpäin, jotta
     paluu Back-painikkeella vie täysin samaan filtteritilaan + scroll-
     positioon kuin alkuperäinen lista. */
  const palautusTila = location.state as {
    listSearch?: string;
    listScrollY?: number;
  } | null;
  const listSearch = palautusTila?.listSearch ?? "";
  const listScrollY = palautusTila?.listScrollY;
  const tunnistautumaton = onTunnistautumaton(kayttaja);
  const ensimmainenAsiakirja = kayttaja.dokumentit
    ? kayttaja.dokumentit[0]
    : null;
  const aktiivinenLaite = kayttaja.laitteet
    ? kayttaja.laitteet.find((l) => l.status === "aktiivinen")
    : null;

  // C11 + 1.6.2026 C3: Saman käyttäjän KAIKKI avoimet/käsittelyssä-tilaiset
  // tukipyynnöt — sisältäen nykyinen tukipyyntö (joka näkyy listalla
  // erottuvasti), jotta virkailija näkee koko kuvan. Workshop 1.6.2026.
  const kaikkiAvoimet =
    naytaSamanKayttajanAvoimet || naytaJonossaTagi
      ? kayttajanTukipyynnot.filter(
          (t) => t.tila === "avoin" || t.tila === "käsittelyssä",
        )
      : [];

  // D1–D3: avoimien ja käsittelyssä-tilaisten pyyntöjen lukumäärä (tagi
  // näytetään vain kun > 0 ja kun komponentti on käyttäjäkortilta).
  const jonossaMaara = naytaJonossaTagi ? kaikkiAvoimet.length : 0;

  // S1 (10.6. B1/B2): bulk-painike kontekstuaalinen.
  // Jos kaikki käyttäjän pyynnöt ovat kirjautuneen virkailijan käsittelyssä
  // → tarjotaan "Palauta kaikki avoimiin". Muuten "Ota kaikki käsittelyyn".
  const kaikkiOmassaKasittelyssa =
    kaikkiAvoimet.length > 0 &&
    kaikkiAvoimet.every((t) => t.kasittelijaUid === virkailija?.id);

  async function handleOtaKaikki() {
    await assignAll(kayttaja.uid).unwrap();
  }

  async function handlePalautaKaikkiAvoimiin() {
    await Promise.all(
      kaikkiAvoimet.map((tp) =>
        unassignTicket(tp.tikettinumero.replace("#", "")).unwrap(),
      ),
    );
  }

  // ─── Poistetun käyttäjän lopullisen poiston päivämäärä ───
  // Sääntö: lopullinen poisto = poistovuoden viimeinen päivä + 5 v (säilytysaika).
  // ESIMERKKI — logiikkaa EI rakennettu: kehittäjä laskee tämän
  // kayttaja.poistettuAika-kentästä. Nyt kovakoodattu vain ulkoasua varten.

  const poistettuAika =
    kayttaja.auditTrail
      ?.filter((entry) => entry.tyyppi === "kayttaja_poistettu")
      .sort(
        (a, b) => new Date(b.aika).getTime() - new Date(a.aika).getTime(),
      )[0]?.aika ?? null;

  const poistettuVuosi = poistettuAika
    ? new Date(poistettuAika).getFullYear()
    : null;
  const lopullinenPoisto = poistettuVuosi ? `31.12.${poistettuVuosi + 5}` : "—";
  const viimeisinTunnistautuminenMapattu = false;

  if (isLoading) return <>{t("common:ladataan")}</>;
  return (
    <Card>
      {/* Poistettu (soft delete) -käyttäjän merkintä sivupalkin ylimpänä →
          näkyy myös tukipyyntönäkymässä. Gate: kayttaja.poistettu.
          DS:n InlineAlert status="warning" (keltainen) — soft delete on tila,
          ei virhe, joten ei error/punainen. Ilman labelText-otsikkoa:
          varoitusikoni + tekstirivit (ikoni tulee statuksesta, ei otsikosta). */}
      {kayttaja.poistettu && (
        <InlineAlert status="warning">
          <PoistettuRivi>
            {t("poistettu.poistettu")} {formatPaiva(poistettuAika, locale)}
          </PoistettuRivi>
          <PoistettuRivi>
            {t("poistettu.lopullinen")} {lopullinenPoisto}
          </PoistettuRivi>
        </InlineAlert>
      )}
      <div>
        {naytaTaysiKortti ? (
          <KayttajaOtsikkoLink
            to={`/kayttajat/${kayttaja.uid}`}
            state={
              fromTukipyyntoId
                ? { fromTukipyyntoId, listSearch, listScrollY }
                : undefined
            }
            aria-label={t("buttons.avaa_taysi_kortti")}
          >
            <SectionLabel as="span">{t("kayttaja")}</SectionLabel>
            <IconArrowRight aria-hidden />
          </KayttajaOtsikkoLink>
        ) : (
          <SectionLabel>{t("kayttaja")}</SectionLabel>
        )}
        <Name>{naytaNimi(kayttaja)}</Name>
        {!tunnistautumaton && (
          <FieldValue style={{ marginTop: "0.25rem" }}>
            {kayttaja.sahkoposti}
          </FieldValue>
        )}

        <FieldRow>
          <FieldLabel>UID</FieldLabel>
          {tunnistautumaton ? (
            <FieldValue>—</FieldValue>
          ) : (
            <UidArea>
              <UidCopy
                uid={kayttaja.customUid}
                todennettu={kayttaja.henkilollisyysTodennettu}
              />
              <VahvistusHint $todennettu={kayttaja.henkilollisyysTodennettu}>
                {t(vahvistusStatus(kayttaja), { ns: "common" })}
              </VahvistusHint>
            </UidArea>
          )}
        </FieldRow>

        {ensimmainenAsiakirja && (
          <>
            <FieldRow>
              <FieldLabel>{t("labels.asiakirja_voimassa")}</FieldLabel>
              <FieldValue>
                {formatPaiva(ensimmainenAsiakirja.vanhenee, locale)}
              </FieldValue>
            </FieldRow>
          </>
        )}

        <FieldRow>
          <FieldLabel>{t("common:kansalaisuus")}</FieldLabel>
          <FieldValue>
            {kayttaja.kansalaisuus
              ? countries.getName(kayttaja.kansalaisuus, i18n.language) +
                " (" +
                alpha2ToAlpha3(kayttaja.kansalaisuus) +
                ")"
              : "-"}{" "}
          </FieldValue>
        </FieldRow>

        <FieldRow>
          <FieldLabel>{t("common:syntymaaika")}</FieldLabel>
          <FieldValue>
            {kayttaja.syntymaaika
              ? kayttaja.syntymaaika.length > 0
                ? formatPaiva(kayttaja.syntymaaika, locale)
                : "-"
              : "-"}
          </FieldValue>
        </FieldRow>

        <FieldRow>
          <FieldLabel>{t("common:sukupuoli")}</FieldLabel>
          <FieldValue>
            {kayttaja.sukupuoli
              ? t(`common:sukupuolet.${kayttaja.sukupuoli}`)
              : t("common:sukupuolet.UNDISCLOSED)")}
          </FieldValue>
        </FieldRow>

        <FieldRow>
          <FieldLabel>{t("labels.rekisteroity")}</FieldLabel>
          <FieldValue>{formatPaiva(kayttaja.rekisteroity, locale)}</FieldValue>
        </FieldRow>
        {viimeisinTunnistautuminenMapattu && (
          <FieldRow>
            <FieldLabel>{t("labels.viimeinen_tunnistautuminen")}</FieldLabel>
            <FieldValue>
              {formatPaiva(kayttaja.viimeisinTunnistautuminen, locale)}
            </FieldValue>
          </FieldRow>
        )}
      </div>

      <Badges>
        {kayttaja.poistettu && (
          <PoistettuTag>{t("tags.poistettu")}</PoistettuTag>
        )}
        <HenkilollisyysBadge todennettu={kayttaja.henkilollisyysTodennettu} />
        {aktiivinenLaite ? (
          <AktiivinenLaiteTag>
            <IconCheck aria-hidden />
            {t("labels.aktiivinen_laite")}
          </AktiivinenLaiteTag>
        ) : (
          <EiAktiivistaLaitettaTag>{t("ei.laitetta")}</EiAktiivistaLaitettaTag>
        )}
        {kayttaja.lukittu && <LukittuTag>{t("tags.lukittu")}</LukittuTag>}
        {jonossaMaara > 0 && (
          <JonossaTag>
            {jonossaMaara}{" "}
            {jonossaMaara === 1
              ? t("tukipyyntoa.yksi")
              : t("tukipyyntoa.monta")}{" "}
            {t("tukipyyntoa.jonossa")}
          </JonossaTag>
        )}
      </Badges>

      {kayttaja.duplikaattiUid && (
        <NoteBox $variant="warning">
          <NoteTitle $variant="warning">{t("duplikaatti")}</NoteTitle>
          <DuplicateLink
            onClick={() => navigate(`/kayttajat/${kayttaja.duplikaattiUid}`)}
          >
            {kayttaja.duplikaattiUid}
          </DuplicateLink>
        </NoteBox>
      )}

      {naytaSamanKayttajanAvoimet && (
        <SamanKayttajanWrap>
          <SamanKayttajanTitle>
            {t("otsikot.saman_kayttajan_avoimet")}
          </SamanKayttajanTitle>
          {kaikkiAvoimet.length === 0 ? (
            <EmptyAvoimet>{t("ei.avoimia")}</EmptyAvoimet>
          ) : (
            <>
              <AvoinList>
                {kaikkiAvoimet.map((tp) => {
                  const nykyinen =
                    tp.tikettinumero.replace("#", "") ===
                    fromTukipyyntoId?.replace("#", "");
                  return (
                    <AvoinRow
                      key={tp.tikettinumero.replace("#", "")}
                      type="button"
                      $nykyinen={nykyinen}
                      disabled={nykyinen}
                      onClick={() => {
                        if (!nykyinen)
                          navigate(
                            `/tukipyynnot/${tp.tikettinumero.replace("#", "")}`,
                            {
                              state: { listSearch, listScrollY },
                            },
                          );
                      }}
                      aria-current={nykyinen ? "page" : undefined}
                    >
                      <AvoinTopRow>
                        <AvoinTiketti $nykyinen={nykyinen}>
                          {tp.tikettinumero}
                        </AvoinTiketti>
                        <AvoinSpacer />
                        <TilaBadge tila={tp.tila} />
                      </AvoinTopRow>
                      <AvoinAihe $nykyinen={nykyinen}>
                        {t(`common:tukipyynto.aihe.${tp.aihe}`)}
                      </AvoinAihe>
                    </AvoinRow>
                  );
                })}
              </AvoinList>
              {kaikkiOmassaKasittelyssa && voikoKasitella ? (
                <Button
                  variant="secondary"
                  fullWidth
                  onClick={handlePalautaKaikkiAvoimiin}
                >
                  {t("buttons.palauta_kaikki")}
                </Button>
              ) : (
                voikoKasitella && (
                  <Button
                    variant="secondary"
                    fullWidth
                    onClick={handleOtaKaikki}
                  >
                    {t("buttons.ota_kaikki")} ({kaikkiAvoimet.length})
                  </Button>
                )
              )}
            </>
          )}
        </SamanKayttajanWrap>
      )}

      {naytaTaysiKortti && (
        <Footer>
          <FullKortinLink
            to={`/kayttajat/${kayttaja.uid}`}
            state={
              fromTukipyyntoId
                ? { fromTukipyyntoId, listSearch, listScrollY }
                : undefined
            }
          >
            {t("buttons.avaa_taysi_kortti")}
            <IconArrowRight aria-hidden />
          </FullKortinLink>
        </Footer>
      )}

      {toiminnotSlot && <ToiminnotWrap>{toiminnotSlot}</ToiminnotWrap>}
    </Card>
  );
}
